import { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  DndContext,
  DragOverlay,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
  pointerWithin,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { KanbanColumn } from "./KanbanColumn"
import { KanbanItem } from "./KanbanItem"
import { fetchTemporaryFilterData } from "../../Utils/helpers/commonhelper"
import { KanbanTableListing } from "./kanbanModules/KanbanTableListing"

export const Kanban = ({
  stages,
  canEdit_canDownload,
  data,
  setData,
  onUpdateLead,
  onAddLead,
  onEditLead,
  onDeleteLead,
  currView,
  visibleColumns,
  filteredLeads,
  onOpenLeadDetails,
  offset,
  limit,
  handlePageChange,
  setOffset,
  setLimit,
  mainGrant, //this will be the leads or deal permission object
  followupGrant, // since the kanbanitem also has the followuup thing
  tempFilterData,
  module_name,
  highlightId,
  totalModuleDataCount,
  isDataLoading,
  pipelineStage,
  pipeline_id,
  refetchData,
  currAdvFilter,
  filterValues,
  tempFilterDataLoading,
  applyFilterDataParams
}) => {


  // State for active dragging item and updating leads
  const [activeItem, setActiveItem] = useState(null)
  const [updatingLeads, setUpdatingLeads] = useState([])
  // finalizing state for data to be rendered on the stages while applying "apply filter" in kanban view
  const kanbanData = currView === "card" && currAdvFilter === "apply" && tempFilterData !== null ? tempFilterData.data : data
  const kanbanTruefalse = currView === "card" && currAdvFilter === "apply" && tempFilterData !== null ? true : false

  const pickedUpTaskColumn = useRef(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  )

  // Transform leads data into stage-based structure for kanban view
  const leads = useMemo(() => {
    if (!Array.isArray(kanbanData)) return {}

    return stages.reduce((acc, stage) => {
      const stageLeads =
        kanbanData.find((item) => item.stage_id === stage.id)?.leads || []
      acc[stage.id] = stageLeads
      return acc
    }, {})
  }, [stages, kanbanData])


  // Get dragging task data for accessibility announcements
  const getDraggingTaskData = (taskId, columnId) => {
    const tasksInColumn = leads[columnId] || []
    const taskPosition = tasksInColumn.findIndex(
      (task) => task.lead_id === taskId
    )
    const column = stages.find((col) => col.id === columnId)
    return {
      tasksInColumn,
      taskPosition,
      column,
    }
  }

  // Check if element has draggable data
  const hasDraggableData = (element) => {
    if (!element) return false
    const data = element.data.current
    return data?.type === "Task"
  }


  const applyFilterRequestId = useRef(0)

  // Hydrate only after listing params match this view. currView updates before
  // applyFilterData does, and an older in-flight response must not overwrite it.
  useEffect(() => {
    const applyData = applyFilterDataParams?.applyFilterData
    const requestId = ++applyFilterRequestId.current
    const isCurrentRequest = () => requestId === applyFilterRequestId.current
    const isFilterValues = applyData?.filterValues?.["filter_values"]?.length > 0
    const paramsMatchCurrentView =
      applyData?.currView === currView &&
      applyData?.offset === offset &&
      applyData?.limit === limit

    if (currAdvFilter !== "apply" || !isFilterValues || !paramsMatchCurrentView) {
      if (currAdvFilter !== "apply" || !isFilterValues) {
        applyData?.setLoading?.(false)
      }
      return
    }

    fetchTemporaryFilterData({
      ...applyData,
      setTempFilterData: (data) => {
        if (!isCurrentRequest()) return
        applyData.setTempFilterData?.(data)
      },
      setLoading: (loading) => {
        if (!isCurrentRequest()) return
        applyData.setLoading?.(loading)
      },
      setFilterState: (state) => {
        if (!isCurrentRequest()) return
        applyData.setFilterState?.(state)
      },
      setFilterDropdown: (open) => {
        if (!isCurrentRequest()) return
        applyData.setFilterDropdown?.(open)
      },
    })
  }, [
    currAdvFilter,
    applyFilterDataParams,
    offset,
    currView,
    limit
  ])


  // Accessibility announcements for drag operations
  const announcements = useMemo(
    () => ({
      onDragStart({ active }) {
        if (!hasDraggableData(active)) return
        if (active.data.current?.type === "Task") {
          pickedUpTaskColumn.current = active.data.current.columnId
          const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
            active.id,
            pickedUpTaskColumn.current
          )
          return `Picked up Task ${active.data.current.task.contact_full_name} at position: ${taskPosition + 1} of ${tasksInColumn.length} in column ${column?.stage_name}`
        }
      },
      onDragOver({ active, over }) {
        if (!hasDraggableData(active) || !hasDraggableData(over)) return
        if (
          active.data.current?.type === "Task" &&
          over.data.current?.type === "Task"
        ) {
          const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
            over.id,
            over.data.current.columnId
          )
          if (over.data.current.columnId !== pickedUpTaskColumn.current) {
            return `Task ${active.data.current.task.contact_full_name} was moved over column ${column?.stage_name} in position ${taskPosition + 1} of ${tasksInColumn.length}`
          }
          return `Task was moved over position ${taskPosition + 1} of ${tasksInColumn.length} in column ${column?.stage_name}`
        }
      },
      onDragEnd({ active, over }) {
        pickedUpTaskColumn.current = null
        if (!hasDraggableData(active) || !hasDraggableData(over)) return
        if (
          active.data.current?.type === "Task" &&
          over.data.current?.type === "Task"
        ) {
          const { tasksInColumn, taskPosition, column } = getDraggingTaskData(
            over.id,
            over.data.current.columnId
          )
          if (over.data.current.columnId !== active.data.current.columnId) {
            return `Task was dropped into column ${column?.stage_name} in position ${taskPosition + 1} of ${tasksInColumn.length}`
          }
          return `Task was dropped into position ${taskPosition + 1} of ${tasksInColumn.length} in column ${column?.stage_name}`
        }
      },
      onDragCancel({ active }) {
        pickedUpTaskColumn.current = null
        if (!hasDraggableData(active)) return
        return `Dragging ${active.data.current?.type} cancelled.`
      },
    }),
    [leads, stages]
  )
  // Handle drag start event - set active item for overlay
  const handleDragStart = (event) => {
    const { active } = event
    if (!hasDraggableData(active)) return

    if (active.data.current?.type === "Task") {
      setActiveItem({
        ...active.data.current.task,
        columnId: active.data.current.columnId,
      })
    }
  }

  // Handle drag end event and update lead stage
  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveItem(null)

    if (!over) return
    const activeId = active.id
    const overId = over.id

    if (!hasDraggableData(active)) return
    if (activeId === overId) return

    const activeData = active.data.current
    const overData = over.data.current

    if (activeData?.type !== "Task") return

    // Determine target column ID
    let targetColumnId
    if (overData?.type === "Task") {
      targetColumnId = overData.columnId
    } else {
      targetColumnId = over.id
    }

    const activeColumn = activeData.columnId

    if (activeColumn === targetColumnId) return

    // Find the dragged lead
    const draggedLead = leads[activeColumn]?.find(
      (lead) => lead.lead_id === activeId
    )
    if (!draggedLead) return

    // Update lead stage via parent component
    if (onUpdateLead) {
      setUpdatingLeads((prev) => [...prev, activeId])

      try {
        await onUpdateLead(draggedLead, targetColumnId, activeColumn)
      } catch (error) {
        // Error handling is done in parent component
      } finally {
        setUpdatingLeads((prev) => prev.filter((id) => id !== activeId))
      }
    }
  }

  // Handle drag over event for local reordering within same column
  const handleDragOver = (event) => {
    const { active, over } = event
    if (!over) return

    const activeId = active.id
    const overId = over.id

    if (activeId === overId) return
    if (!hasDraggableData(active)) return

    const activeData = active.data.current
    const overData = over.data.current

    if (activeData?.type !== "Task") return

    // Handle reordering within same column
    if (
      overData?.type === "Task" &&
      activeData.columnId === overData.columnId
    ) {
      const activeIndex =
        leads[activeData.columnId]?.findIndex((t) => t.lead_id === activeId) ??
        -1
      const overIndex =
        leads[overData.columnId]?.findIndex((t) => t.lead_id === overId) ?? -1

      if (activeIndex === -1 || overIndex === -1) return

      setData((prev) => {
        return prev.map((stageData) => {
          if (stageData.stage_id === activeData.columnId) {
            const newLeads = [...stageData.leads]
            const [removed] = newLeads.splice(activeIndex, 1)
            newLeads.splice(overIndex, 0, removed)
            return { ...stageData, leads: newLeads }
          }
          return stageData
        })
      })
    }
  }

  // Handle add lead click from column - passes stage ID to parent
  const handleAddClick = (stageId) => {
    onAddLead && onAddLead(stageId)
  }

  return (
    <div>

      {
        currView === "card" ? (
          // Kanban Card View
          <DndContext
            accessibility={{ announcements }}
            sensors={sensors}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            collisionDetection={pointerWithin}
            onDragOver={handleDragOver}
          >
            <div
              style={{
                overflowX: "auto",
                overflowY: "visible",
                padding: "0px 0",
                height: "calc(100vh - 11rem)",
                marginBottom: "0px",
                display: "flex",
              }}
            >
              <div
                className=" d-flex gap-2 listStyleForKanban"
                style={{ overflowY: "scroll", scrollBehavior: "smooth" }}
              >
                {stages.map((stage) => (
                  <SortableContext
                    key={stage.id}
                    items={(leads[stage.id] || []).map((i) => i.lead_id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <KanbanColumn
                      id={stage.id}
                      title={stage.stage_name}
                      items={leads[stage.id] || []}
                      onAddClick={handleAddClick}
                      updatingLeads={updatingLeads}
                      onEditLead={onEditLead}
                      onDeleteLead={onDeleteLead}
                      onOpenLeadDetails={onOpenLeadDetails}
                      stageColor={stage.color}
                      mainGrant={mainGrant}
                      followupGrant={followupGrant}
                      highlightId={highlightId}
                      module_name={module_name}
                      pipeline_id={pipeline_id}
                    />
                  </SortableContext>
                ))}
              </div>
            </div>
            <style jsx>
              {`
              .listStyleForKanban::-webkit-scrollbar {
                height: 6px;
              }
              .listStyleForKanban::-webkit-scrollbar-track {
                background: white;
                border-radius: 10px;
              }
              .listStyleForKanban::-webkit-scrollbar-thumb {
                background: #e0e0e0;
                border-radius: 10px;
              }
              .listStyleForKanban::-webkit-scrollbar-thumb:hover {
                background: #c9c9c9;
              }
            `}
            </style>

            {/* Drag overlay for visual feedback during drag operations */}
            {createPortal(
              <DragOverlay>
                {activeItem && (
                  <KanbanItem
                    item={activeItem}
                    columnId={activeItem.columnId}
                    dragOverlay
                  />
                )}
              </DragOverlay>,
              document.body
            )}
          </DndContext>
        ) : (
          <>
            {/* // Table List View */}
            <KanbanTableListing
              data={data}
              visibleColumns={visibleColumns}
              tempFilterData={tempFilterData}
              filteredLeads={filteredLeads}
              canEdit_canDownload={canEdit_canDownload}
              limit={limit}
              setLimit={setLimit}
              offset={offset}
              setOffset={setOffset}
              highlightId={highlightId}
              onDeleteLead={onDeleteLead}
              onEditLead={onEditLead}
              onOpenLeadDetails={onOpenLeadDetails}
              module_name={module_name}
              handlePageChange={handlePageChange}
              totalModuleDataCount={totalModuleDataCount}
              isDataLoading={isDataLoading}
              filterDropPipelineStage={pipelineStage}
              pipeline_id={pipeline_id}
              refetchData={refetchData}
              mainGrant={mainGrant}
              currAdvFilter={currAdvFilter}
              applyFilterDataParams={applyFilterDataParams}
              tempFilterDataLoading={tempFilterDataLoading}
            />
          </>
        )
      }
    </div >
  )
}